import { openDB, IDBPDatabase } from "idb";
import {
  CartOperation,
  OperationType,
  AddPayload,
  isAddPayload,
  isUpdatePayload,
  isRemovePayload,
} from "./types";

const DB_NAME = "meucontrole-cart-sync";
const STORE_NAME = "operations";
const VERSION = 1;

export class OperationQueue {
  private db: IDBPDatabase | null = null;
  private listeners: Set<() => void> = new Set();
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    try {
      this.db = await openDB(DB_NAME, VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
            store.createIndex("status", "status");
            store.createIndex("timestamp", "timestamp");
          }
        },
      });
    } catch (error) {
      console.error("[OperationQueue] Failed to initialize IndexedDB:", error);
      this.db = null;
    }
  }

  private async ensureInit(): Promise<void> {
    if (!this.db) {
      await this.init();
    }
  }

  async enqueue(operation: CartOperation): Promise<void> {
    await this.ensureInit();

    const absorbed = await this.compactOperations(operation);

    if (!absorbed && this.db) {
      await this.db.put(STORE_NAME, operation);
    }

    this.notifyListeners();
  }

  async getPending(): Promise<CartOperation[]> {
    await this.ensureInit();

    if (!this.db) return [];

    const all = await this.db.getAll(STORE_NAME);
    return all
      .filter((op) => op.status === "PENDING" || op.status === "FAILED")
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  async getAll(): Promise<CartOperation[]> {
    await this.ensureInit();

    if (!this.db) return [];

    return this.db.getAll(STORE_NAME);
  }

  async getById(id: string): Promise<CartOperation | undefined> {
    await this.ensureInit();

    if (!this.db) return undefined;

    return this.db.get(STORE_NAME, id);
  }

  async markInFlight(ids: string[]): Promise<void> {
    await this.ensureInit();

    if (!this.db || ids.length === 0) return;

    const tx = this.db.transaction(STORE_NAME, "readwrite");
    for (const id of ids) {
      const op = await tx.store.get(id);
      if (op) {
        op.status = "IN_FLIGHT";
        await tx.store.put(op);
      }
    }
    await tx.done;

    this.notifyListeners();
  }

  async markCompleted(
    ids: string[],
    serverIdMap?: Map<string, string>
  ): Promise<void> {
    await this.ensureInit();

    if (!this.db || ids.length === 0) return;

    const tx = this.db.transaction(STORE_NAME, "readwrite");
    for (const id of ids) {
      const op = await tx.store.get(id);
      if (op) {
        op.status = "COMPLETED";
        if (serverIdMap && op.type === "ADD" && isAddPayload(op.payload)) {
          const serverId = serverIdMap.get(op.payload.tempId);
          if (serverId) {
            (op.payload as AddPayload).serverId = serverId;
          }
        }
        await tx.store.put(op);
      }
    }
    await tx.done;

    setTimeout(() => this.pruneCompleted(), 5000);

    this.notifyListeners();
  }

  async markFailed(ids: string[], error: string): Promise<void> {
    await this.ensureInit();

    if (!this.db || ids.length === 0) return;

    const tx = this.db.transaction(STORE_NAME, "readwrite");
    for (const id of ids) {
      const op = await tx.store.get(id);
      if (op) {
        op.status = "FAILED";
        op.lastError = error;
        op.retryCount += 1;
        await tx.store.put(op);
      }
    }
    await tx.done;

    this.notifyListeners();
  }

  async resetToRetry(ids: string[]): Promise<void> {
    await this.ensureInit();

    if (!this.db || ids.length === 0) return;

    const tx = this.db.transaction(STORE_NAME, "readwrite");
    for (const id of ids) {
      const op = await tx.store.get(id);
      if (op && op.status === "IN_FLIGHT") {
        op.status = "PENDING";
        await tx.store.put(op);
      }
    }
    await tx.done;

    this.notifyListeners();
  }

  async remove(id: string): Promise<void> {
    await this.ensureInit();

    if (!this.db) return;

    await this.db.delete(STORE_NAME, id);
    this.notifyListeners();
  }

  async clear(): Promise<void> {
    await this.ensureInit();

    if (!this.db) return;

    await this.db.clear(STORE_NAME);
    this.notifyListeners();
  }

  private async compactOperations(newOp: CartOperation): Promise<boolean> {
    await this.ensureInit();

    if (!this.db) return false;

    const pending = await this.getPending();
    const tx = this.db.transaction(STORE_NAME, "readwrite");
    let absorbed = false;

    for (const existingOp of pending) {
      const compactResult = this.shouldCompact(existingOp, newOp);

      if (compactResult === "remove") {
        await tx.store.delete(existingOp.id);
      } else if (compactResult === "remove-and-absorb") {
        await tx.store.delete(existingOp.id);
        absorbed = true; 
      } else if (compactResult === "merge-into-add") {
        if (isAddPayload(existingOp.payload) && isUpdatePayload(newOp.payload)) {
          const newQuantity = newOp.payload.changes.quantity;
          if (newQuantity !== undefined) {
            existingOp.payload.item.quantity = newQuantity;
            existingOp.payload.item.itemTotalPrice =
              existingOp.payload.item.unitPrice * newQuantity;
            await tx.store.put(existingOp);
            absorbed = true; 
          }
        }
      }
    }

    await tx.done;
    return absorbed;
  }

  private shouldCompact(
    existing: CartOperation,
    incoming: CartOperation
  ): "remove" | "merge-into-add" | "remove-and-absorb" | false {
    if (incoming.type === "CLEAR") {
      return "remove";
    }

    const existingItemId = this.getItemId(existing);
    const incomingItemId = this.getItemId(incoming);

    if (existingItemId && incomingItemId && existingItemId === incomingItemId) {
      if (incoming.type === "REMOVE" && existing.type === "ADD") {
        return "remove-and-absorb";
      }
      if (incoming.type === "REMOVE" && existing.type === "UPDATE") {
        return "remove";
      }
      if (incoming.type === "UPDATE" && existing.type === "ADD") {
        return "merge-into-add";
      }
      if (incoming.type === "UPDATE" && existing.type === "UPDATE") {
        return "remove";
      }
    }

    return false;
  }

  private getItemId(op: CartOperation): string | null {
    switch (op.type) {
      case "ADD":
        return isAddPayload(op.payload) ? op.payload.tempId : null;
      case "UPDATE":
        return isUpdatePayload(op.payload) ? op.payload.itemId : null;
      case "REMOVE":
        return isRemovePayload(op.payload) ? op.payload.itemId : null;
      default:
        return null;
    }
  }

  private async pruneCompleted(): Promise<void> {
    await this.ensureInit();

    if (!this.db) return;

    try {
      const all = await this.db.getAll(STORE_NAME);
      const tx = this.db.transaction(STORE_NAME, "readwrite");

      for (const op of all) {
        if (op.status === "COMPLETED") {
          await tx.store.delete(op.id);
        }
      }

      await tx.done;
    } catch (error) {
      console.error("[OperationQueue] Failed to prune completed:", error);
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((l) => {
      try {
        l();
      } catch (error) {
        console.error("[OperationQueue] Listener error:", error);
      }
    });
  }

  async getPendingCount(): Promise<number> {
    const pending = await this.getPending();
    return pending.length;
  }

  async hasPending(): Promise<boolean> {
    const count = await this.getPendingCount();
    return count > 0;
  }

  async getByType(type: OperationType): Promise<CartOperation[]> {
    await this.ensureInit();

    if (!this.db) return [];

    const all = await this.db.getAll(STORE_NAME);
    return all.filter((op) => op.type === type && op.status === "PENDING");
  }
}

let queueInstance: OperationQueue | null = null;

export function getOperationQueue(): OperationQueue {
  if (!queueInstance) {
    queueInstance = new OperationQueue();
  }
  return queueInstance;
}

export function resetOperationQueue(): void {
  queueInstance = null;
}
